"use client";

import { useSearchParams } from "next/navigation";

import { Icons } from "@/components/shared/icons";
import MaxWidthWrapper from "@/components/shared/max-width-wrapper";
import { buttonVariants } from "@/components/ui/button";
import { marketingConfig } from "@/config/marketing";
import { useScroll } from "@/hooks/use-scroll";
import { Link } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import * as m from "@/paraglide/messages";
import { useAuth } from "@/services/context/auth/AuthContext";

import LogoText from "../global/LogoText";
import { ThemeSwitcher } from "../global/theme-switcher";
import LanguageSwitcher from "../language-dropdown";

interface NavBarProps {
  scroll?: boolean;
  large?: boolean;
}

export function NavBar({ scroll = false }: NavBarProps) {
  const searchParam = useSearchParams();
  const { user } = useAuth();
  const scrolled = useScroll(50);
  const redirectTo = searchParam.get("redirect");
  const links = marketingConfig(m).mainNav;

  return (
    <>
      <header
        className={`sticky top-0 z-40 flex w-full justify-center dark:bg-zinc-900 bg-background/60 backdrop-blur-xl transition-all ${
          scroll ? (scrolled ? "border-b" : "bg-transparent") : "border-b"
        }`}
      >
        <MaxWidthWrapper className="flex h-14 items-center justify-between py-4">
          <div className="flex gap-6 lg:gap-10">
            <Link href="/" className="flex items-center space-x-2">
              <LogoText className="h-7" />
            </Link>
            <>
              {links && links.length > 0 ? (
                <nav className="hidden gap-6 lg:flex">
                  {links.map((item: any, index: number) => (
                    <Link
                      key={index}
                      href={item.disabled ? "#" : item.href}
                      prefetch={true}
                      className={cn(
                        "flex capitalize items-center text-lg font-medium transition-colors hover:text-foreground/80 sm:text-sm",
                        item.disabled && "cursor-not-allowed opacity-80",
                      )}
                    >
                      {item.title}
                    </Link>
                  ))}
                </nav>
              ) : null}
            </>
          </div>

          <div className="flex items-center space-x-3 lg:me-0 me-3">
            <ThemeSwitcher />
            <LanguageSwitcher />
            {!user?.email ? (
              <Link
                className={cn(
                  buttonVariants({ variant: "default", size: "sm" }),
                  "hidden gap-2 px-4 text-xs lg:flex",
                )}
                href={
                  redirectTo != null
                    ? `/signin?redirect=${redirectTo}`
                    : "/signin"
                }
              >
                <span>{m.sign_in()}</span>
                <Icons.arrowRight className="size-3" />
              </Link>
            ) : (
              <Link
                className={cn(
                  buttonVariants({ variant: "default", size: "sm" }),
                  "hidden gap-2 px-4 lg:flex",
                )}
                href="/dashboard"
              >
                <span className="normal-case">{m.dashboard()}</span>
                <Icons.arrowRight className="size-4" />
              </Link>
            )}
          </div>
        </MaxWidthWrapper>
      </header>
    </>
  );
}
